import React from 'react';
import {
    Table,
    Button,
    Card,
    Space,
    message,
    Modal,
    Drawer,
    Input,
    Select,
    Popconfirm,
    Empty,
    Pagination,
    Spin,
} from 'antd';
import {
    ReloadOutlined,
    SearchOutlined,
    FolderOpenOutlined,
    FolderOutlined,
    UserAddOutlined,
    TagOutlined,
} from '@ant-design/icons';
import {
    listVideos,
    scanVideos,
    deleteVideo,
    assignVideoAccess,
    revokeVideoAccess,
    listVideoAccessUsers,
    updateVideoMeta,
    listVideoCategories,
    listVideoTags,
} from '../../api/video';
import { listUsers } from '../../api/sys_user';
import { getUser } from '../../utils/auth';
import {
    ROLES,
    displayRoleName,
    canScanVideos,
    canAssignVideo,
    canDeleteVideo,
    canViewVideoAccess,
    canSetVideoMeta,
    canManageVideoDict,
    isSuperAdmin,
    isAdmin,
} from '../../utils/role';
import { openVideoPlayWindow } from '../../utils/videoPlay';
import { resumeAllPreviews } from '../../utils/previewPauseBus';
import VideoCard from './VideoCard';
import VideoCategoryManager from './VideoCategoryManager';
import VideoTagManager from './VideoTagManager';
import ScanPathPicker from '../ScanPathPicker/ScanPathPicker';
import MediaAccessUserPicker from '../MediaAccess/MediaAccessUserPicker';
import { drawerWidth, modalWidth } from '../../utils/responsive';
import './VideoBrowser.css';

const DEFAULT_PAGE_SIZE = 24;

class VideoBrowser extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            videos: [],
            loading: false,
            keyword: '',
            page: 1,
            pageSize: DEFAULT_PAGE_SIZE,
            scanOpen: false,
            scanning: false,
            accessOpen: false,
            accessVideo: null,
            accessUsers: [],
            accessLoading: false,
            assignPickerOpen: false,
            assignableUsers: [],
            assignableLoading: false,
            assigning: false,
            categoryId: null,
            tagId: null,
            categories: [],
            tags: [],
            categoryMgrOpen: false,
            tagMgrOpen: false,
            metaOpen: false,
            metaVideo: null,
            metaCategoryId: null,
            metaTagIds: [],
            metaSaving: false,
        };
    }

    componentDidMount() {
        this._mounted = true;
        this.loadVideos();
        this.loadDict();
    }

    componentWillUnmount() {
        this._mounted = false;
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
        }
    }

    /** 预览/播放成功后刷新列表，同步后端自愈的 status（库中 1 → 实际文件存在） */
    scheduleListRefresh = () => {
        if (this.refreshTimer) {
            clearTimeout(this.refreshTimer);
        }
        this.refreshTimer = setTimeout(() => this.loadVideos(), 600);
    };

    handlePreviewVerified = () => {
        this.scheduleListRefresh();
    };

    componentDidUpdate(prevProps, prevState) {
        if (prevState.categoryId !== this.state.categoryId || prevState.tagId !== this.state.tagId) {
            this.loadVideos();
        }
        if (prevState.keyword !== this.state.keyword) {
            this.setState({ page: 1 });
        }
        const filtered = this.getFilteredVideos();
        const maxPage = Math.max(1, Math.ceil(filtered.length / this.state.pageSize) || 1);
        if (this.state.page > maxPage) {
            this.setState({ page: maxPage });
        }
    }

    loadDict = async () => {
        const user = getUser();
        if (!canSetVideoMeta(user)) return;
        if (!this._mounted) return;
        if (this.dictLoading) {
            this.dictRefreshPending = true;
            return;
        }
        this.dictAbort = new AbortController();
        this.dictLoading = true;
        const { signal } = this.dictAbort;
        try {
            const [cats, tgs] = await Promise.all([
                listVideoCategories({ signal }),
                listVideoTags({ signal }),
            ]);
            if (!this._mounted) return;
            this.setState({ categories: cats, tags: tgs });
        } catch (e) {
            if (e?.name === 'AbortError') {
                return;
            }
            if (!this._mounted) return;
            this.setState({ categories: [], tags: [] });
            message.error({
                key: 'video-dict-load',
                content: e.message || '加载分类/标签失败',
            });
        } finally {
            this.dictLoading = false;
            if (this.dictRefreshPending && this._mounted) {
                this.dictRefreshPending = false;
                this.loadDict();
            }
        }
    };

    loadVideos = async () => {
        if (!this._mounted) return;
        const { categoryId, tagId } = this.state;
        const requestKey = `${categoryId ?? ''}|${tagId ?? ''}`;
        if (this.listLoading) {
            if (this.listRequestKey === requestKey) {
                this.listRefreshPending = true;
                return;
            }
            this.listAbort?.abort();
        }
        this.listAbort = new AbortController();
        this.listRequestKey = requestKey;
        this.listLoading = true;
        const { signal } = this.listAbort;
        this.setState({ loading: true });
        try {
            const list = await listVideos({
                categoryId: categoryId ?? undefined,
                tagId: tagId ?? undefined,
                signal,
            });
            if (!this._mounted) return;
            this.setState({ videos: list });
        } catch (e) {
            if (e?.name === 'AbortError') {
                return;
            }
            if (!this._mounted) return;
            message.error({
                key: 'video-list-load',
                content: e.message || '加载视频列表失败',
            });
            this.setState({ videos: [] });
        } finally {
            this.listLoading = false;
            if (!signal.aborted && this._mounted) {
                this.setState({ loading: false });
            }
            if (this.listRefreshPending && this._mounted) {
                this.listRefreshPending = false;
                this.loadVideos();
            }
        }
    };

    getFilteredVideos = () => {
        const { videos, keyword } = this.state;
        const kw = keyword.trim().toLowerCase();
        if (!kw) return videos;
        return videos.filter(
            (v) =>
                (v.fileName && v.fileName.toLowerCase().includes(kw)) ||
                (v.fileExt && v.fileExt.toLowerCase().includes(kw)) ||
                (v.filePath && v.filePath.toLowerCase().includes(kw))
        );
    };

    getPaginatedVideos = () => {
        const filtered = this.getFilteredVideos();
        const { page, pageSize } = this.state;
        const start = (page - 1) * pageSize;
        return filtered.slice(start, start + pageSize);
    };

    loadAssignableUsers = async () => {
        const currentUser = getUser();
        this.setState({ assignableLoading: true });
        try {
            const all = await listUsers();
            const list = all.filter((u) => {
                if (u.status !== '0') return false;
                if (isAdmin(currentUser)) return u.roleKey === ROLES.USER;
                return u.roleKey === ROLES.USER || u.roleKey === ROLES.ADMIN;
            });
            this.setState({ assignableUsers: list });
            return list;
        } catch {
            this.setState({ assignableUsers: [] });
            return [];
        } finally {
            this.setState({ assignableLoading: false });
        }
    };

    handleScan = async (path) => {
        const trimmed = path.trim();
        if (!trimmed) {
            message.warning('请选择扫描路径');
            return;
        }
        this.setState({ scanning: true });
        try {
            const result = await scanVideos(trimmed);
            message.success(
                `扫描完成：新增 ${result.inserted ?? 0}，更新 ${result.updated ?? 0}，跳过 ${result.skipped ?? 0}`
            );
            this.setState({ scanOpen: false });
            await this.loadVideos();
        } catch (e) {
            message.error(e.message || '扫描失败');
        } finally {
            this.setState({ scanning: false });
        }
    };

    openAccessDrawer = async (record) => {
        this.setState({
            accessVideo: record,
            accessOpen: true,
            assignPickerOpen: false,
            accessLoading: true,
        });
        await this.loadAssignableUsers();
        try {
            const users = await listVideoAccessUsers(record.id);
            this.setState({ accessUsers: Array.isArray(users) ? users : [] });
        } catch (e) {
            message.error(e.message || '加载分配列表失败');
            this.setState({ accessUsers: [] });
        } finally {
            this.setState({ accessLoading: false });
        }
    };

    openAssignPicker = async () => {
        await this.loadAssignableUsers();
        this.setState({ assignPickerOpen: true });
    };

    handleBatchAssign = async (userIds) => {
        const { accessVideo } = this.state;
        if (!accessVideo || userIds.length === 0) {
            return;
        }
        this.setState({ assigning: true });
        try {
            const results = await Promise.allSettled(
                userIds.map((userId) => assignVideoAccess(accessVideo.id, userId))
            );
            const ok = results.filter((r) => r.status === 'fulfilled').length;
            const fail = results.length - ok;
            if (ok > 0) {
                message.success(`已成功分配 ${ok} 个用户`);
            }
            if (fail > 0) {
                message.warning(`${fail} 个用户分配失败（可能已分配或无权限）`);
            }
            this.setState({ assignPickerOpen: false });
            const users = await listVideoAccessUsers(accessVideo.id);
            this.setState({ accessUsers: users });
        } catch (e) {
            message.error(e.message || '分配失败');
        } finally {
            this.setState({ assigning: false });
        }
    };

    handleRevoke = async (userId) => {
        const { accessVideo } = this.state;
        try {
            await revokeVideoAccess(accessVideo.id, userId);
            message.success('已撤销观看权限');
            const users = await listVideoAccessUsers(accessVideo.id);
            this.setState({ accessUsers: users });
        } catch (e) {
            message.error(e.message || '撤销失败');
        }
    };

    handlePlay = (record) => {
        const win = openVideoPlayWindow(record);
        if (!win) {
            message.warning('无法打开新页面，请允许浏览器打开新标签页后重试');
            return;
        }
        const watchClose = setInterval(() => {
            if (win.closed) {
                clearInterval(watchClose);
                resumeAllPreviews();
                this.scheduleListRefresh();
            }
        }, 400);
    };

    handleDelete = async (record) => {
        try {
            await deleteVideo(record.id);
            message.success('已删除视频记录');
            await this.loadVideos();
        } catch (e) {
            message.error(e.message || '删除失败');
        }
    };

    confirmDelete = (record) => {
        Modal.confirm({
            title: '确认删除该视频记录？',
            content: '仅删除库内记录，不删除磁盘上的视频文件。',
            okText: '删除',
            okType: 'danger',
            cancelText: '取消',
            onOk: () => this.handleDelete(record),
        });
    };

    openMetaDrawer = (record) => {
        this.setState({
            metaVideo: record,
            metaCategoryId: record.categoryId ?? null,
            metaTagIds: (record.tags || []).map((t) => t.id),
            metaOpen: true,
        });
    };

    handleSaveMeta = async () => {
        const { metaVideo, metaCategoryId, metaTagIds } = this.state;
        if (!metaVideo) return;
        this.setState({ metaSaving: true });
        try {
            await updateVideoMeta(metaVideo.id, {
                categoryId: metaCategoryId,
                tagIds: metaTagIds,
            });
            message.success('视频分类与标签已保存');
            this.setState({ metaOpen: false, metaVideo: null });
            await this.loadVideos();
        } catch (e) {
            message.error(e.message || '保存失败');
        } finally {
            this.setState({ metaSaving: false });
        }
    };

    render() {
        const currentUser = getUser();
        const {
            loading,
            page,
            pageSize,
            scanOpen,
            scanning,
            accessOpen,
            accessVideo,
            accessUsers,
            accessLoading,
            assignPickerOpen,
            assignableUsers,
            assignableLoading,
            assigning,
            categoryId,
            tagId,
            categories,
            tags,
            categoryMgrOpen,
            tagMgrOpen,
            metaOpen,
            metaVideo,
            metaCategoryId,
            metaTagIds,
            metaSaving,
        } = this.state;

        const filteredVideos = this.getFilteredVideos();
        const paginatedVideos = this.getPaginatedVideos();

        const roleHint = isSuperAdmin(currentUser)
            ? '超级管理员：可查看全部视频、扫描入库、分配观看'
            : isAdmin(currentUser)
              ? '管理员：可查看全部视频、向普通用户分配观看'
              : '普通用户：仅可播放已分配的视频';

        const emptyDescription = canScanVideos(currentUser)
            ? '暂无视频，请先扫描入库'
            : '暂无可观看的视频';

        return (
            <div className="video-browser">
                <Card className="video-browser__card" bordered={false}>
                    <div className="video-browser__header">
                        <div>
                            <h2 className="video-browser__title">视频浏览</h2>
                            <p className="video-browser__subtitle">{roleHint}</p>
                        </div>
                        <Space wrap>
                            {canSetVideoMeta(currentUser) && (
                                <div className="video-browser__filters">
                                    <span className="video-browser__filter-label">
                                        <FolderOutlined aria-hidden />
                                        分类
                                    </span>
                                    <Select
                                        allowClear
                                        placeholder="全部分类"
                                        style={{ width: 140 }}
                                        value={categoryId}
                                        onChange={(v) => this.setState({ categoryId: v ?? null })}
                                        options={categories.map((c) => ({
                                            value: c.id,
                                            label: c.categoryName,
                                        }))}
                                    />
                                    <span className="video-browser__filter-label">
                                        <TagOutlined aria-hidden />
                                        标签
                                    </span>
                                    <Select
                                        allowClear
                                        placeholder="全部标签"
                                        style={{ width: 140 }}
                                        value={tagId}
                                        onChange={(v) => this.setState({ tagId: v ?? null })}
                                        options={tags.map((t) => ({
                                            value: t.id,
                                            label: t.tagName,
                                        }))}
                                    />
                                </div>
                            )}
                            <Input.Search
                                placeholder="搜索视频名称"
                                allowClear
                                style={{ width: 280 }}
                                enterButton={<SearchOutlined />}
                                onSearch={(v) => this.setState({ keyword: v })}
                                onChange={(e) => {
                                    if (!e.target.value) this.setState({ keyword: '' });
                                }}
                            />
                            <Button icon={<ReloadOutlined />} onClick={this.loadVideos}>
                                刷新
                            </Button>
                            {canScanVideos(currentUser) && (
                                <Button
                                    type="primary"
                                    icon={<FolderOpenOutlined />}
                                    onClick={() => this.setState({ scanOpen: true })}
                                >
                                    扫描入库
                                </Button>
                            )}
                            {canManageVideoDict(currentUser) && (
                                <>
                                    <Button
                                        icon={<FolderOutlined />}
                                        onClick={() => this.setState({ categoryMgrOpen: true })}
                                    >
                                        分类管理
                                    </Button>
                                    <Button
                                        icon={<TagOutlined />}
                                        onClick={() => this.setState({ tagMgrOpen: true })}
                                    >
                                        标签管理
                                    </Button>
                                </>
                            )}
                        </Space>
                    </div>

                    <Spin spinning={loading}>
                        {filteredVideos.length === 0 && !loading ? (
                            <Empty className="video-browser__empty" description={emptyDescription} />
                        ) : (
                            <>
                                <p className="video-browser__count">共 {filteredVideos.length} 个视频</p>
                                <div className="video-grid">
                                    {paginatedVideos.map((video) => (
                                        <VideoCard
                                            key={video.id}
                                            video={video}
                                            onPlay={this.handlePlay}
                                            onAssign={this.openAccessDrawer}
                                            onDelete={this.confirmDelete}
                                            onEditMeta={this.openMetaDrawer}
                                            onPreviewVerified={this.handlePreviewVerified}
                                            showAssign={canViewVideoAccess(currentUser)}
                                            showDelete={canDeleteVideo(currentUser)}
                                            showEditMeta={canSetVideoMeta(currentUser)}
                                        />
                                    ))}
                                </div>
                                {filteredVideos.length > 0 && (
                                    <div className="video-browser__pagination">
                                        <Pagination
                                            current={page}
                                            pageSize={pageSize}
                                            total={filteredVideos.length}
                                            showSizeChanger
                                            showTotal={(t) => `共 ${t} 个`}
                                            pageSizeOptions={['12', '24', '48', '96']}
                                            onChange={(p, size) => {
                                                this.setState({ page: p, pageSize: size });
                                            }}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </Spin>
                </Card>

                <ScanPathPicker
                    open={scanOpen}
                    mediaType="video"
                    title="扫描视频入库"
                    hint="在服务端 video-scan-roots 白名单内浏览目录，选择要扫描的文件夹或单个视频文件"
                    scanning={scanning}
                    onCancel={() => this.setState({ scanOpen: false })}
                    onScan={this.handleScan}
                />

                <Drawer
                    title={accessVideo ? `观看权限：${accessVideo.fileName}` : '观看权限'}
                    width={drawerWidth(480)}
                    open={accessOpen}
                    onClose={() => this.setState({ accessOpen: false, accessVideo: null })}
                    destroyOnClose
                >
                    {canAssignVideo(currentUser) && (
                        <Button
                            type="primary"
                            icon={<UserAddOutlined />}
                            onClick={this.openAssignPicker}
                            style={{ marginBottom: 16 }}
                        >
                            选择用户分配
                        </Button>
                    )}
                    <Table
                        size="small"
                        rowKey="userId"
                        loading={accessLoading}
                        scroll={{ x: 'max-content' }}
                        dataSource={accessUsers}
                        pagination={false}
                        columns={[
                            { title: '用户', dataIndex: 'loginId' },
                            {
                                title: '角色',
                                dataIndex: 'roleKey',
                                render: (k) => displayRoleName({ roleKey: k }),
                            },
                            {
                                title: '操作',
                                key: 'op',
                                render: (_, row) => (
                                    <Popconfirm
                                        title="确认撤销？"
                                        onConfirm={() => this.handleRevoke(row.userId)}
                                    >
                                        <Button type="link" size="small" danger>
                                            撤销
                                        </Button>
                                    </Popconfirm>
                                ),
                            },
                        ]}
                        locale={{ emptyText: '暂未分配给任何用户' }}
                    />
                </Drawer>

                <MediaAccessUserPicker
                    open={assignPickerOpen}
                    title="选择用户分配观看权限"
                    actionLabel="观看"
                    resourceLabel={accessVideo?.fileName}
                    users={assignableUsers}
                    assignedUserIds={accessUsers.map((u) => u.userId)}
                    loading={assignableLoading}
                    confirming={assigning}
                    onCancel={() => this.setState({ assignPickerOpen: false })}
                    onConfirm={this.handleBatchAssign}
                />

                <Drawer
                    title={
                        metaVideo ? `设置分类与标签：${metaVideo.fileName}` : '设置分类与标签'
                    }
                    width={drawerWidth(420)}
                    open={metaOpen}
                    onClose={() => this.setState({ metaOpen: false, metaVideo: null })}
                    destroyOnClose
                    extra={
                        <Button type="primary" loading={metaSaving} onClick={this.handleSaveMeta}>
                            保存
                        </Button>
                    }
                >
                    <Space direction="vertical" style={{ width: '100%' }} size="middle">
                        <Card
                            size="small"
                            className="video-browser__meta-section video-browser__meta-section--category"
                            title={
                                <Space size={4}>
                                    <FolderOutlined />
                                    分类
                                </Space>
                            }
                        >
                            <p style={{ margin: '0 0 12px', color: '#64748b', fontSize: 13 }}>
                                每个视频只能属于一个分类
                            </p>
                            <Select
                                allowClear
                                placeholder="选择分类"
                                style={{ width: '100%' }}
                                value={metaCategoryId}
                                onChange={(v) => this.setState({ metaCategoryId: v ?? null })}
                                options={categories.map((c) => ({
                                    value: c.id,
                                    label: c.categoryName,
                                }))}
                            />
                        </Card>
                        <Card
                            size="small"
                            className="video-browser__meta-section video-browser__meta-section--tag"
                            title={
                                <Space size={4}>
                                    <TagOutlined />
                                    标签
                                </Space>
                            }
                        >
                            <p style={{ margin: '0 0 12px', color: '#64748b', fontSize: 13 }}>
                                可同时添加多个标签
                            </p>
                            <Select
                                mode="multiple"
                                allowClear
                                placeholder="选择标签"
                                style={{ width: '100%' }}
                                value={metaTagIds}
                                onChange={(v) => this.setState({ metaTagIds: v })}
                                options={tags.map((t) => ({
                                    value: t.id,
                                    label: t.tagName,
                                }))}
                            />
                        </Card>
                    </Space>
                </Drawer>

                <Modal
                    title="视频分类管理"
                    open={categoryMgrOpen}
                    onCancel={() => this.setState({ categoryMgrOpen: false })}
                    footer={null}
                    width={modalWidth(640)}
                    destroyOnClose
                >
                    <VideoCategoryManager
                        open={categoryMgrOpen}
                        onChanged={() => {
                            this.loadDict();
                            this.loadVideos();
                        }}
                    />
                </Modal>

                <Modal
                    title="视频标签管理"
                    open={tagMgrOpen}
                    onCancel={() => this.setState({ tagMgrOpen: false })}
                    footer={null}
                    width={modalWidth(640)}
                    destroyOnClose
                >
                    <VideoTagManager
                        open={tagMgrOpen}
                        onChanged={() => {
                            this.loadDict();
                            this.loadVideos();
                        }}
                    />
                </Modal>
            </div>
        );
    }
}

export default VideoBrowser;
